import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { BookkeepingAdjustmentType } from '~/types/general-bookkeeping';

const riscontoAttivoAccountAtom = atomWithStorage<string | null>(
  'bookkeeping-adjustment-account-risconto-attivo',
  null,
  undefined,
  { getOnInit: true },
);
const riscontoPassivoAccountAtom = atomWithStorage<string | null>(
  'bookkeeping-adjustment-account-risconto-passivo',
  null,
  undefined,
  { getOnInit: true },
);
const rateoAttivoAccountAtom = atomWithStorage<string | null>(
  'bookkeeping-adjustment-account-rateo-attivo',
  null,
  undefined,
  { getOnInit: true },
);
const rateoPassivoAccountAtom = atomWithStorage<string | null>(
  'bookkeeping-adjustment-account-rateo-passivo',
  null,
  undefined,
  { getOnInit: true },
);
const fatturaDaEmettereAccountAtom = atomWithStorage<string | null>(
  'bookkeeping-adjustment-account-fattura-da-emettere',
  null,
  undefined,
  { getOnInit: true },
);
const fatturaDaRicevereAccountAtom = atomWithStorage<string | null>(
  'bookkeeping-adjustment-account-fattura-da-ricevere',
  null,
  undefined,
  { getOnInit: true },
);

export function useAdjustmentAccountMemory() {
  const [riscontoAttivo, setRiscontoAttivo] = useAtom(riscontoAttivoAccountAtom);
  const [riscontoPassivo, setRiscontoPassivo] = useAtom(riscontoPassivoAccountAtom);
  const [rateoAttivo, setRateoAttivo] = useAtom(rateoAttivoAccountAtom);
  const [rateoPassivo, setRateoPassivo] = useAtom(rateoPassivoAccountAtom);
  const [fatturaDaEmettere, setFatturaDaEmettere] = useAtom(fatturaDaEmettereAccountAtom);
  const [fatturaDaRicevere, setFatturaDaRicevere] = useAtom(fatturaDaRicevereAccountAtom);

  // AMMORTAMENTO is generated by the backend, never created through this flow, so it has
  // no account to remember: the entry is inert and recall/save are never called with it.
  const noop = () => {};

  const memory: Record<BookkeepingAdjustmentType, string | null> = {
    [BookkeepingAdjustmentType.RISCONTO_ATTIVO]: riscontoAttivo,
    [BookkeepingAdjustmentType.RISCONTO_PASSIVO]: riscontoPassivo,
    [BookkeepingAdjustmentType.RATEO_ATTIVO]: rateoAttivo,
    [BookkeepingAdjustmentType.RATEO_PASSIVO]: rateoPassivo,
    [BookkeepingAdjustmentType.FATTURA_DA_EMETTERE]: fatturaDaEmettere,
    [BookkeepingAdjustmentType.FATTURA_DA_RICEVERE]: fatturaDaRicevere,
    [BookkeepingAdjustmentType.AMMORTAMENTO]: null,
  };

  const setters: Record<BookkeepingAdjustmentType, (id: string | null) => void> = {
    [BookkeepingAdjustmentType.RISCONTO_ATTIVO]: setRiscontoAttivo,
    [BookkeepingAdjustmentType.RISCONTO_PASSIVO]: setRiscontoPassivo,
    [BookkeepingAdjustmentType.RATEO_ATTIVO]: setRateoAttivo,
    [BookkeepingAdjustmentType.RATEO_PASSIVO]: setRateoPassivo,
    [BookkeepingAdjustmentType.FATTURA_DA_EMETTERE]: setFatturaDaEmettere,
    [BookkeepingAdjustmentType.FATTURA_DA_RICEVERE]: setFatturaDaRicevere,
    [BookkeepingAdjustmentType.AMMORTAMENTO]: noop,
  };

  function recall(type: BookkeepingAdjustmentType): string | null {
    return memory[type];
  }

  function save(type: BookkeepingAdjustmentType, accountId: string) {
    setters[type](accountId);
  }

  return { recall, save };
}
